<template>
<div class="chapter">
<div class="inner_page">
<section epub:type="chapter">
<header>
<h1 class="topictitle1" id="ch9"><a href="#/nav#nch9"><span class="section_number">Chapter 2</span><br/><span class="title">From the Graves of Eden</span></a></h1>
</header>
<div class="epi_group">
<p class="epigraph">Looking back across time, through all those fossils . . . I can equate natural selection with just two members of the Hindu
               Triad—with Siva the destroyer and Vishnu the preserver, but not with Brahma the creator.
            </p>
<p class="epigraph_attribution">—Charles Lyell</p>
<p class="sep"> </p>
<p class="epigraph">Past, present, and future are only an illusion, albeit a stubborn one.</p>
<p class="epigraph_attribution">—Albert Einstein</p>
</div>
<div class="date_group">
<p class="date">Two Days Later</p>
<p class="date">June 25, 1948</p>
<p class="location">Metropolitan Museum of Natural History, NYC</p>
</div>
<p>The fifth-floor office door was open, so Major Patrick Hendry decided that clearing his throat would be an effective way to
            announce his arrival. R. J. MacCready and two familiar “museum types” had their backs turned to him and were examining what
            appeared to be a pair of football-sized skulls.
         </p>
<p>As Hendry’s pharyngeal house cleaning abruptly turned into a prolonged, phlegmy cough, the trio turned toward him, wearing
            an assortment of disgusted looks.
         </p>
<p>“I’ll have some of that on a cracker,” Mac said, eliciting additional groans from the pixyish woman in black sitting to his
            right.
         </p>
<p>“Go ahead, Mac, make fun,” Hendry said, after finally catching his breath. He was carrying a shoe-box-sized package and made
            an exaggerated showing of gently placing it down on MacCready’s desk.
         </p>
<p>“Well, Major, that <i>was</i> a rather unique entrance,” said Patricia Wynters. She had flipped up a pair of jeweler’s loupe-equipped eyeglasses and was
            now wearing a genuinely concerned expression. “Are you ill?”
         </p>
<p>“Sounds like he needs an oil change,” grumbled a white-haired man, who quickly turned his attention back to the skulls.</p>
<p>“Nice to see you too, Knight,” Major Hendry grumbled back.</p>
<p>Seventy-three-year-old Charles R. Knight was the world’s foremost artist of prehistoric life. Without turning around, he granted
            Hendry a wave with his cigarette.
         </p>
<p>“Personally, I think <i>both</i> of you should quit smoking,” Patricia said quietly.
         </p>
<p>“Second that,” Mac added, now waving his arms in a vain attempt to break up the smoke screen that Hendry and Knight were laying
            down.
         </p>
<p>Ignoring Patricia’s and Mac’s comments, Hendry moved in closer to an enamel tray holding the skulls. “Prehistoric horses again,
            huh?”
         </p>
<p>“Yeah, and no touching,” Mac added. Though the major had certainly improved over the years, Mac remained mindful of past trespasses
            that included the conversion of a triceratops into a <i>bi</i>ceratops, and most infamously, tripping over and fracturing three of T. rex’s toes. Some of Knight’s colleagues believed a
            convergence of disease and an upheaval of the world’s oceans had killed the dinosaurs. “If so,” Knight had assured one of
            them, “it took Hendry to finish the job.”
         </p>
<p>Putting down his cigar and leaning in toward the specimens, the major placed both hands behind his back—assuming the “museum
            looking” position Mac had demonstrated for him on too many occasions for Hendry’s liking.
         </p>
<p>“<i>Mesohippus</i> and <i>Parahippus</i>, huh?” he asked.
         </p>
<p>“You got it,” Mac said, with an appreciative nod.</p>
<p>“Show-off,” Knight muttered under his breath. “Ask him what species they are.”</p>
<p>Patricia ignored her grumpy friend. “We’re off to central Brazil next month, Major,” she announced, excitedly. “Yanni too.”</p>
<p>Instead of responding, Hendry flashed her a tight-lipped smile and nodded slightly.</p>
<p>“What?” Mac asked quickly, his voice carrying a hint of alarm.</p>
<p>The major gestured toward the specimens. “Those formerly extinct horses you’re looking to study have been around a long time—right,
            Mac?”
         </p>
<p>“About fifteen million years,” MacCready replied. “And yeah, that’s a lot longer than anyone believed possible.”</p>
<p>Hendry took a deep breath, then let it out slowly. “Well, then—”</p>
<p>“You<i> can’t </i>be serious?” Mac said, interrupting what he knew was bound to be bad news.
         </p>
<p>Major Hendry said nothing.</p>
<p>“Come on, Pat. I mean . . . I’m on quasi-semi-active duty.”</p>
<p>Hendry cupped a hand behind one ear. “What was that last thing?” The major turned to Knight and Wynters. “He <i>did</i> say ‘active duty.’ Am I right?”
         </p>
<p>The researchers stared back at him, blankly, so the major continued. “I’m <i>sure</i> there was an ‘active’ in there somewhere.”
         </p>
<p>“But the permits, the equipment, the . . . the—”</p>
<p>Hendry shook his head, still silent but wearing an expression that said, <i>Save it</i>, <i>Mac</i>. He turned back to the package he’d brought and opened it. “This came for you in the mail today,” he said, gently removing
            some packing material before spreading out several items on an empty tray. “They’re from Greece. Your pal Tse-lin dug ’em
            up.”
         </p>
<p>“Wang Tse-lin? And what are <i>you</i> doing with it?”
         </p>
<p>“Let’s just say, someone in your mailroom gave me a call when it came in. I did them a little favor by making the delivery
            myself.”
         </p>
<p>The three scientists moved in for a closer look. Hendry had laid out a pair of strange-looking hand tools—obviously ancient.
            “They’re made of volcanic glass,” he said, watching as the trio reacted with something several notches below astonishment.
         </p>
<p>Knight was the first to turn away, adjusting the easel that held his latest reconstruction on canvas—a prehistoric horse species
            with beautiful golden eyes.
         </p>
<p>Mac picked up an obsidian blade. “Umm . . . these are nice, Pat. But you can find stone tools all around the Mediterranean.”</p>
<p>Instead of responding, Major Hendry broke into a smile that Mac had seen only rarely—and he hated it. It said, <i>Gotcha!</i></p>
<p>“What?” Mac said.</p>
<p>Hendry replied by lifting away another layer of cardboard and cotton packing material. Then he carefully withdrew an object
            and held it out to MacCready and Patricia.
         </p>
<p>“Well, here’s your tool maker, smart guy.”</p>
<p>Reacting to the odd silence that followed, Charles R. Knight turned around, adjusted his glasses, and stood so quickly that
            he knocked his easel and brushes to the floor.
         </p>
<p>“Holy shit!” he cried. “You have <i>got</i> to be kidding me.”
         </p>
<p>“No kidding, Chuck,” Hendry replied. Then, turning to Mac, he handed him an envelope with two folded pages sticking out and
            jerked a thumb toward Knight. “Now, before <i>you</i> get excited and start knocking stuff over, I need you to read this.”
         </p>
<p>Mac read it quickly and handed the telegram back.</p>
<p>“What is it?” Patricia asked, concerned.</p>
<p>R. J. MacCready remained as silent as the Sphinx—his face giving away nothing. <i>Well, Pat,</i> he thought. <i>I wonder what new level of hell you’re dropping me into this time.</i></p>
<p class="txtb2"> </p>
<p>Mac decided even before he repacked the stone tools into Hendry’s box and tucked it under an arm that there was no choice
            but to cancel his expedition to Brazil. The telegram Hendry had received from Cousteau in the eastern Mediterranean did not
            leave much time to tear up the equipment checklist for a South American expedition and reprovision for the new island destination.
            Stepping out onto Eighty-First Street and glancing toward a subway entrance, he found it difficult to plan a way through the
            train delays that had been plaguing the system lately. Reaching for his wallet, he voted for speed over an inexpensive subway
            token and hailed a cab heading downtown on Central Park West.
         </p>
<p>“Castle Garden, Battery Park,” he told the driver, then sat back and closed his eyes. Mac knew that Yanni had been looking
            forward to the Brazil expedition. He also knew that she preferred to receive bad news promptly.
         </p>
<p>All things considered, she took it better than he anticipated.</p>
<p>“And no helicopters, right?”</p>
<p>Mac grimaced. “Yeah, well, Hendry didn’t exactly mention helicopters.”</p>
<p>As she spoke, Yanni Thorne was simultaneously treading water and adjusting a hydrophone array at one end of a large saltwater
            pool. Several months after the death of the Central Park Menagerie’s last elephant, Yanni had applied for and been transferred
            to a Navy-affiliated project at what used to be the New York Aquarium.
         </p>
<p>“So where to?” she asked, switching off the underwater microphones. At the deep end of the pool, an enormous shape responded
            by making a sharp turn. A five-foot-long, gleaming white hump broke the surface and began steaming toward her at high speed.
         </p>
<p>Mac took a protectively reflexive step forward, watching as the beluga whale he’d nicknamed “Moby-Dick Jr.” generated a large
            V-shaped wake. It had taken Mac several visits to get used to the immensity of the creature—which Yanni’s late husband might
            have referred to as “a Buick with fins.” In truth, Mac knew there was no real cause for concern. The animal’s trainer was,
            after all, Yanni.
         </p>
<p>“An island off the coast of Greece,” Mac responded, as the whale pulled up and raised its domed head out of the water. The
            humans were suddenly facing a mouthful of peg-like teeth.
         </p>
<p>“Which island?” she asked, before turning to address the cetacean. “Hold on a minute there, big guy.”</p>
<p>Yanni swam to the side of the pool and after gracefully hauling herself out, Mac handed her a towel.</p>
<p>“Santorini,” he said, before gesturing to a poolside bucket. “So what’s Junior havin’ for treats today?”</p>
<p>“Coupla bluefish and some mackerel,” she said, reaching into a mélange of sea life, fresh from the Fulton Fish Market. “There’s
            an active volcano there, right?”
         </p>
<p>“Yeah, semiactive I’d call it. Not like—”</p>
<p>“And a civil war going on, too?”</p>
<p>“Yeah, it’s a weird one.”</p>
<p>“Well then gimme the thirty-second version.”</p>
<p>“Let’s see. On the right we’ve got the Greek government army. Their leaders were exiled during the war, and while they were
            gone things went to shit. Of course, that’s who we’re backing. And on the left, there are the communist partisans.”
         </p>
<p>“Who weaseled their way into the vacuum left by the Nazis.”</p>
<p>“You got it,” Mac said.</p>
<p>“Lemme guess. Backed by Stalin and his mob.”</p>
<p>“You’d think so, right?” Mac said, flashing the smile he reserved for the rare occasions when Yanni got something wrong. “But
            actually, no—it’s been mostly Yugoslavia and their own commie heartthrob, Tito. Stalin actually opposes the fight.”
         </p>
<p>“Why’s that?”</p>
<p>“Hates Tito, plus Stalin’s picking his fights carefully. And Greece ain’t it.”</p>
<p>“Got it,” Yanni said.</p>
<p>Mac watched as Yanni flipped the tail half of a “cocktail blue” into Junior’s waiting maw, which chomped closed with a crunch.
            “We should be okay, though. Most of that political shit’s taking place on the Greek mainland and north of Athens.”
         </p>
<p>“Oh yeah, sounds completely safe,” Yanni said, sounding somewhat less than convinced. “So why Santorini then?”</p>
<p>“Well, you remember the white stuff and the red stuff in Tibet?”</p>
<p>“You’re such a zoologist,” she replied. “You mean the fungus that could speed up evolution?”</p>
<p>“Yeah, among other things.”</p>
<p>“So?”</p>
<p>“So, a coupla weeks ago Hendry gets a message from Tse-lin, of all people.”</p>
<p>“At Santorini? Wasn’t he tracking down fossils in Africa?”</p>
<p>“He was. Until he gets sent to Greece.”</p>
<p>“Wouldn’t a thunk there’d be much paleoanthropology to be done near a volcano.”</p>
<p>“No, you wouldn’t think so. But there he was—following a lead about weird fossils—digging away nice and peaceful like.”</p>
<p>“And then?”</p>
<p>“And then he learns about some big ruckus that’s got everyone worked up. Long story short, sounds to him like the red stuff.”</p>
<p>Yanni shot him an incredulous look. “Coming out of the volcano?”</p>
<p>“Sort of,” Mac replied. “In the water—spewing out of a thermal vent on the lagoon floor.”</p>
<p>“This is the <i>same</i> microbe we found in Tibet?”
         </p>
<p>“Something similar but I can’t imagine a fungus from Tibet blowing out of a thermal vent all the way in the Mediterranean.
            Tse-lin says it acts the same—cures, healing, the whole shebang.”
         </p>
<p>“As in ‘The world’s greatest cure or its deadliest weapon.’”</p>
<p>“Yeah, that shebang,” Mac replied with a nod. “Darwin amok.”</p>
<p>Yanni let out a deep breath and glanced back at Junior, who was clearly anticipating his next course. In response, she tossed
            a baseball-bat-length eel toward the middle of the pool. Then she crossed her arms above her head, signaling that snack time
            was over.
         </p>
<p>“Look,” Mac continued, “we knew we couldn’t keep a lid on this forever—that we’d be drawn back in somehow. I guess we just
            convinced ourselves we had more time, stopped believing it could be so soon.”
         </p>
<p>“Great,” Yanni said, staring across the pool. “What else?”</p>
<p>Mac gave a funeral laugh. “Well, this is the part where things go to hell. Seems three French divers head down to investigate
            the discovery . . .”
         </p>
<p>“Yeah, and?”</p>
<p>“And two of ’em got torn apart.”</p>
<p>“What, sharks?” Yanni replied. “I <i>hate</i> sharks.”
         </p>
<p>“No. Not sharks. Apparently something a bit more—well, more exotic. I happen to know the guy who survived—solid credentials.
            And he says this is something <i>none</i> of us have seen before.”
         </p>
<p>“Wonderful.”</p>
<p>“Knew you’d think so.”</p>
<p>“So is Tse-lin still there?”</p>
<p>Mac nodded. “Barely. Until Hendry doubly reassured him we were going in, he was lookin’ to get out of Dodge.”</p>
<p>Yanni shot him a quizzical look.</p>
<p>“Santorini,” Mac corrected himself.</p>
<p>“You blame him?” Yanni said. “The return of our ‘red stuff,’ something tearing divers apart.” She shook her head. “Sounds
            like the perfect vacation spot.”
         </p>
<p>“And don’t forget the civil war,” Mac added cheerily.</p>
<p>“Sure,” Yanni said. “We can’t have the locals gettin’ along.”</p>
<p>“Nah, that would set an uncomfortable precedent,” Mac said. “Hey, look on the bright side: at least we won’t be freezing our
            butts off this time, right?”
         </p>
<p>“I suppose,” Yanni said, watching skeptically as Mac unfolded the top of a cardboard box he’d brought.</p>
<p>“But there <i>is</i> one more thing.”
         </p>
<p>“How’d I guess?”</p>
<p>“No, this one’s okay. In fact it’ll give us an interesting cover story.”</p>
<p>Mac gently removed a small, beautifully preserved fossil skull from the box.</p>
<p>“That looks familiar.”</p>
<p>“It is. It’s a chimp, or something close to it. Tse-lin thinks it’s some lost branch of the bonobos. Knight agrees with him.”</p>
<p>“That’s nice—those little pygmy guys, right?” She lifted the skull and gently turned it over. “But why’s that so important?”</p>
<p>“The kicker’s what our Chinese pal found <i>alongside</i> the bones.”
         </p>
<p>“Go on,” Yanni said, allowing impatience to creep into her voice.</p>
<p>Mac reached into the box again, withdrew a small object, and held it out to her. Then he gestured toward the skull. “Seems
            like Cheeta made this.”
         </p>
<p>“Ancient chimps crafting stone tools. Get the hell out of here!”</p>
<p>Mac replied with a wry smile.</p>
<p>“How long ago?”</p>
<p>“Oh, we’re thinking something like five million years—give or take a million.”</p>
<p>“Holy shit,” Yanni said. “You <i>are </i>kiddin’ me, right?”
         </p>
<p>Mac stifled a laugh.</p>
<p class="txtb2"> </p>
<div class="date_group">
<p class="date">5 Million Years to Santorini</p>
</div>
<p>When the great sea was already ancient, of an age most people scarcely dream, continents were being reshaped and the stage
            was being set for a focus of human forces. Two million years before the arrival of Santorini’s tool makers, Africa had been
            a raft of land, adrift on a collision course with southern Spain. At the point of impact between Morocco and Gibraltar, the
            seabed crumpled and a new mountain range bulged four thousand feet into the clouds, forming a natural dam that completely
            sealed off the Atlantic from the Mediterranean.
         </p>
<p>As rocks and continents measured time, the Gibraltar Dam formed and the entire Mediterranean Sea began to dry out in a geologic
            heartbeat; yet, from Mac’s and Yanni’s perspective on time, that single heartbeat would have lasted twenty thousand generations.
            Aside from the occasional earthquake, no ancestral humans could have noticed the change—given even a whole lifetime of watching,
            even as it happened before their eyes.
         </p>
<p>The universe was filled with such contrasts, and in Mac’s generation, Harold “Doc” Edgerton reveled in them. Having invented
            a method for filming the first split second of an atomic bomb test in ultra-slow motion, he next time-lapsed entire years
            into seconds. Thus did he reveal the previously unobserved wonder of autumn reds and yellows alternating with flashes of snow
            and bursts of green while his cameras slowly panned across Massachusetts landscapes. By the time Mac and Yanni departed a
            Grumman airfield on Long Island for the isle of Santorini, Edgerton had already begun advising stop-motion film animator George
            Pal, who would soon transform H. G. Wells’s novel about a time traveler into a feature-length film depicting a journey across
            802,701 years.
         </p>
<p>To Edgerton and Pal, even the slow and stately transitions of ancient Gibraltar could be perceived as a symphony of motion.
            To them, the centuries required for the entire Mediterranean to evaporate behind the dam and form a canyon deeper and wider
            than any the planet had known in more than a hundred million years could be viewed across the span of a minute. Below Santorini
            and Crete, and as far south as Egypt, deep blue waters flashed to desert. Forests flowed in, spreading across the land like
            puffs of green vapor. And then, blue floodwaters. And desert again. Forests.
         </p>
<p>By 5.4 million years<span class="small_caps"> b.c.,</span> a chancy sort of balance had been achieved in the uneasy marriage between two continental plates. For more than 600,000 years,
            now, the Gibraltar Dam stood strong. Two miles beneath the limestone plain on which the pyramids would make their stand against
            time, the Nile flowed north along the Mediterranean floor. It fed scores of lakes and oases, before being stopped near Crete
            and the deep waters of the Devil’s Hole. At the hole’s edge, great fissures yawned open. Volcanic fountains sent forth streams
            of water laced so thickly with red microbes that they grew like wool on the rocks.
         </p>
<p>In the incomparably dense, humid, and richly oxygenated air, relatives of clams, snails, and squids evolved to fill niches
            never occupied by their kind before. Insects and spiders failed to thrive in the great Mediterranean canyon. There were no
            ants, no termites, and no mosquitoes. The mollusks had seen to that.
         </p>
<p>Sea butterflies transitioned from graceful swimmers into the air—first as gliders, then exhibiting powered flight. With no
            aerial competitors, they diversified into myriad shapes and sizes—convergence imparting them with color patterns that would
            have seemed familiar to any twentieth-century birdwatcher.
         </p>
<p>But the land two miles below Spain, Greece, and Africa remained inhospitable to outsiders and so the ancestral elephants that
            occasionally migrated down from the green Sahara highlands did not stay for very long. They were smart enough to know better.
         </p>
</section>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Chapter_2_From_the_Graves.vue?2dfa92c3